import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/** BB4 (besluit en GO Frank 4 november 2026): de prijs per cloudmodel, door jou zelf ingevuld.
 *
 *  ALLEEN BIJ EEN CLOUDMODEL: bij een lokaal model kost een vraag niets, dus dan staat dit blok er niet.
 *  GEEN VERZONNEN PRIJZEN: de velden beginnen leeg, en zonder ingevulde prijs noemt NovaMund alleen tokens.
 */
export const LOKAAL = ["ollama", ""];
export const isCloud = (aanbieder) => !LOKAAL.includes(String(aanbieder || "").trim().toLowerCase());

export default function ModelPrijzen({ aanbieder, model }) {
  const { t } = useTaal();
  const [prijzen, setPrijzen] = useState([]);
  const [invoer, setInvoer] = useState(null);
  const [bezig, setBezig] = useState(false);
  const cloud = isCloud(aanbieder);

  useEffect(() => {
    if (!cloud) return undefined;
    let weg = false;
    axios.get(`${API}/settings/prijzen`)
      .then((r) => { if (!weg) setPrijzen(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!weg) setPrijzen([]); });
    return () => { weg = true; };
  }, [cloud]);

  const huidig = prijzen.find((p) => p.aanbieder === String(aanbieder).toLowerCase() && p.model === model);
  useEffect(() => {
    setInvoer({ in: huidig ? String(huidig.per_miljoen_in) : "", uit: huidig ? String(huidig.per_miljoen_uit) : "",
                valuta: huidig?.valuta || "EUR" });
  }, [huidig?.id, aanbieder, model]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cloud || !invoer) return null;

  const bewaar = async () => {
    setBezig(true);
    try {
      const r = await axios.put(`${API}/settings/prijzen`, {
        aanbieder, model, per_miljoen_in: invoer.in, per_miljoen_uit: invoer.uit, valuta: invoer.valuta });
      setPrijzen((oud) => [...oud.filter((p) => p.id !== r.data.id), r.data]);
      toast.success(t("Prijs bewaard"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("De prijs is niet bewaard"));
    } finally {
      setBezig(false);
    }
  };

  const haalWeg = async (id) => {
    try {
      await axios.delete(`${API}/settings/prijzen/${id}`);
      setPrijzen((oud) => oud.filter((p) => p.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("De prijs is niet weggehaald"));
    }
  };

  return (
    <div className="space-y-2 border rounded-lg p-3" data-testid="model-prijzen">
      <Label>{t("Prijs van dit model")}</Label>
      <p className="text-xs text-muted-foreground">
        {t("Vul zelf in wat {model} kost, per miljoen tokens. NovaMund vult hier niets in; zonder prijs zie je onder een antwoord alleen de tokens.", { model: model || "" })}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Input inputMode="decimal" value={invoer.in} placeholder={t("in, per miljoen")} data-testid="prijs-in"
               onChange={(e) => setInvoer({ ...invoer, in: e.target.value })} />
        <Input inputMode="decimal" value={invoer.uit} placeholder={t("uit, per miljoen")} data-testid="prijs-uit"
               onChange={(e) => setInvoer({ ...invoer, uit: e.target.value })} />
      </div>
      <div className="flex gap-2 items-center">
        <select className="text-xs border rounded p-1 bg-background" value={invoer.valuta} data-testid="prijs-valuta"
                onChange={(e) => setInvoer({ ...invoer, valuta: e.target.value })}>
          <option value="EUR">EUR</option>
          <option value="USD">USD</option>
        </select>
        <Button size="sm" onClick={bewaar} data-testid="prijs-bewaar"
                disabled={bezig || !model || invoer.in === "" || invoer.uit === ""}>
          {t("Prijs bewaren")}
        </Button>
      </div>
      {prijzen.length > 0 && (
        <ul className="text-xs space-y-1" data-testid="prijs-lijst">
          {prijzen.map((p) => (
            <li key={p.id} className="flex justify-between gap-2 break-words">
              <span>{t("{aanbieder} / {model}: {pin} in, {puit} uit, {valuta} per miljoen", { aanbieder: p.aanbieder, model: p.model, pin: p.per_miljoen_in, puit: p.per_miljoen_uit, valuta: p.valuta })}</span>
              <button type="button" className="underline shrink-0" onClick={() => haalWeg(p.id)}
                      data-testid={`prijs-weg-${p.id}`}>{t("Weghalen")}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
